import { useEffect, useRef, useState } from "react";
import { BookOpen, MessageCircle } from "lucide-react";
import { useLocation } from "react-router-dom";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Mascot, type MascotMood } from "@/components/mascot";
import { useAIAssistant } from "@/contexts/AIAssistantContext";
import { useDictionaryStore } from "@/stores/dictionary";
import { useIsMobile } from "@/hooks/use-mobile";
import { focusRingClass, slateCardClass } from "@/lib/card-styles";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

/**
 * The one floating control in the bottom-right corner.
 *
 * The Learning Coach and the Dictionary are both "a thing you open when you
 * need it", so they share a single entry point: one mascot, and a menu of what
 * it can open. Two separate buttons used to sit stacked in the same corner and
 * read as clutter rather than as two halves of one idea.
 *
 * The mascot is the control — no pill behind it. It sits at 64px (mascot size
 * "sm"), which is already a comfortable tap target, so the button needs no
 * background of its own.
 *
 * Popover, not DropdownMenu: a menu opens on *pointerdown* and closes on
 * focus-out, so on touch (and in some desktop browsers) a tap opened and
 * closed it inside one frame — clicking the mascot did nothing at all. A
 * popover opens on click, is non-modal, and never moves the page.
 */
export function AssistantFab() {
  const { t } = useTranslation();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [mood, setMood] = useState<MascotMood>("idle");
  const moodTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const choose = (action: () => void) => {
    setOpen(false);
    action();
  };

  // The mascot reacts to being tapped — a startle, then curiosity while it has
  // something to say, then back to rest. That reaction is the whole feedback:
  // the button itself stays perfectly still and keeps the same colour, because
  // a resizing or recolouring control next to a character that is already
  // moving reads as a glitch.
  useEffect(() => {
    if (open) {
      setMood("surprised");
      moodTimer.current = setTimeout(() => setMood("curious"), 650);
    } else {
      setMood("idle");
    }
    return () => {
      if (moodTimer.current) clearTimeout(moodTimer.current);
    };
  }, [open]);

  const {
    isOpen: isAssistantOpen,
    isEnabled: isAssistantEnabled,
    isAuthenticated: isAssistantAuthenticated,
    widgetMode,
    openWidget: openAssistant,
    closeWidget: closeAssistant,
  } = useAIAssistant();
  const isDictionaryOpen = useDictionaryStore((s) => s.isOpen);
  const openDictionary = useDictionaryStore((s) => s.openWidget);
  const closeDictionary = useDictionaryStore((s) => s.closeWidget);

  const isPracticePage =
    location.pathname.startsWith("/session/") &&
    location.pathname.endsWith("/practice");
  const isAIPage =
    location.pathname === "/my-topics/ai" ||
    location.pathname.startsWith("/my-topics/ai/");
  const isMyTopicsPage =
    location.pathname === "/my-topics" ||
    location.pathname.startsWith("/my-topics/");
  // The Lotería table is a game surface: the mascot would sit on the tabla and
  // the answer controls on a phone, and the speaking takeover is full-screen.
  const isRollAndTalkPage = location.pathname.startsWith("/roll-and-talk");

  // Practice surfaces, the AI page and My Topics each own the whole screen.
  // So does any open panel — both dock into the same corner the FAB lives in.
  if (isPracticePage || isAIPage || isMyTopicsPage || isRollAndTalkPage) return null;
  if (isAssistantOpen || isDictionaryOpen) return null;

  const isSidebarMode = widgetMode === "sidebar" && !isMobile;
  // The vocabulary desk hides the mobile tab bar (see `MobileBottomNav`'s
  // HIDE_PREFIXES), so the FAB can sit on the page floor there instead of
  // floating where the tab bar would be.
  const hidesMobileTabBar = location.pathname.startsWith("/vocabulary/practice");

  return (
    <div
      className={cn(
        "fixed z-50",
        // Phones: 8px of air over the page floor on the vocab desk, and 8px
        // over the 56px tab bar everywhere else. Desktop: the usual corner.
        isMobile
          ? hidesMobileTabBar
            ? "bottom-2 right-2"
            : "bottom-16 right-2"
          : "bottom-4 right-4",
        // The docked assistant panel steals 380px from the page; follow it so
        // the FAB never ends up sitting underneath it.
        isSidebarMode && "md:right-[calc(var(--ai-sidebar-width,0px)+1rem)]"
      )}
    >
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={t("common.mascot.openLabel", "Open Learning Coach or Dictionary")}
            aria-expanded={open}
            className={cn(
              "flex items-center justify-center rounded-2xl",
              focusRingClass
            )}
          >
            <Mascot mood={mood} size="sm" />
          </button>
        </PopoverTrigger>

        <PopoverContent
          side="top"
          align="end"
          sideOffset={14}
          collisionPadding={16}
          className={cn(
            // A quote bubble, spoken by the mascot: the bottom-right corner is
            // pulled into a tail that points down at the character, so the
            // options read as coming out of it rather than as a system menu.
            "relative w-60 rounded-2xl rounded-br-md p-1.5",
            slateCardClass,
            "shadow-[0_12px_34px_-10px_rgba(15,23,42,0.22)]",
            "data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0",
            "data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95",
            "data-[state=open]:slide-in-from-bottom-1 data-[state=closed]:slide-out-to-bottom-1",
            "data-[state=open]:duration-150 data-[state=closed]:duration-100",
            "origin-bottom-right"
          )}
        >
          {/* The tail: a rotated square masked by the bubble border, so it
              carries the same hairline as the panel it grows out of. */}
          <span
            aria-hidden="true"
            className={cn(
              "pointer-events-none absolute -bottom-[5px] right-[18px] h-2.5 w-2.5 rotate-45",
              "border-b border-r border-stone-200/80 bg-white",
              "dark:border-stone-800 dark:bg-stone-900"
            )}
          />
          <FabAction
            icon={<MessageCircle className="h-4 w-4" />}
            label="AI assistant"
            hint={
              isAssistantAuthenticated
                ? "Ask, revise or research"
                : "Sign in to use the assistant"
            }
            onSelect={() =>
              choose(() => {
                closeDictionary();
                openAssistant();
              })
            }
          />
          <FabAction
            icon={<BookOpen className="h-4 w-4" />}
            label="Dictionary"
            hint="Look up a word or phrase"
            onSelect={() =>
              choose(() => {
                closeAssistant();
                openDictionary();
              })
            }
          />

          {!isAssistantEnabled && (
            <p className="px-3 pb-1 pt-2 text-[11px] leading-snug text-stone-500">
              The assistant is off in settings. The dictionary still works.
            </p>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}

interface FabActionProps {
  icon: React.ReactNode;
  label: string;
  hint: string;
  onSelect: () => void;
}

function FabAction({ icon, label, hint, onSelect }: FabActionProps) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "relative flex w-full cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5 text-left",
        "transition-colors hover:bg-stone-100 focus-visible:bg-stone-100 focus-visible:outline-none",
        "dark:hover:bg-stone-800 dark:focus-visible:bg-stone-800"
      )}
    >
      <span
        aria-hidden="true"
        className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary"
      >
        {icon}
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-medium text-stone-900 dark:text-stone-100">
          {label}
        </span>
        <span className="block truncate text-xs text-stone-500 dark:text-stone-400">
          {hint}
        </span>
      </span>
    </button>
  );
}